import React from 'react';
import { FiCheckCircle, FiArrowRight, FiBriefcase, FiTrendingUp, FiUsers, FiTarget, FiAward, FiBarChart } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';

const LinkedInMarketingPage = () => {
  const features = [
    {
      icon: FiBriefcase,
      title: "Company Page",
      description: "Professional company page setup and optimization to showcase your brand to business professionals"
    },
    {
      icon: FiTrendingUp,
      title: "Content Strategy",
      description: "Strategic content creation and posting schedule designed for B2B engagement and lead generation"
    },
    {
      icon: FiUsers,
      title: "Lead Generation",
      description: "LinkedIn lead gen forms and campaigns to capture high-quality B2B leads directly"
    },
    {
      icon: FiTarget,
      title: "B2B Targeting",
      description: "Advanced targeting by job title, company, industry, seniority, and skills for precise reach"
    },
    {
      icon: FiAward,
      title: "LinkedIn Ads",
      description: "Sponsored content, message ads, and dynamic ads to reach decision-makers effectively"
    },
    {
      icon: FiBarChart,
      title: "Analytics & Reporting",
      description: "Track engagement, follower growth, lead quality, and campaign performance with detailed reports"
    }
  ];

  const pricingPlans = [
    {
      name: "Basic",
      price: "₹18,000",
      period: "/month",
      description: "Perfect for businesses starting with LinkedIn",
      features: [
        "Company Page Optimization",
        "12 Posts per Month",
        "Content Calendar",
        "Basic Engagement",
        "Monthly Analytics Report",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Professional",
      price: "₹35,000",
      period: "/month",
      description: "Ideal for B2B companies scaling LinkedIn presence",
      features: [
        "Everything in Basic",
        "20 Posts per Month",
        "LinkedIn Ad Campaigns",
        "Lead Generation Setup",
        "Employee Advocacy Program",
        "Bi-weekly Performance Reports",
        "Phone + Email Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large B2B organizations needing full LinkedIn strategy",
      features: [
        "Everything in Professional",
        "Unlimited Posts",
        "Advanced Ad Campaigns",
        "InMail Campaigns",
        "Personal Branding for Executives",
        "LinkedIn Employee Training",
        "Weekly Strategy Calls",
        "Dedicated Account Manager"
      ],
      highlighted: false
    }
  ];

  const whyChooseUs = [
    {
      title: "B2B Marketing Experts",
      description: "Specialized team with proven track record in LinkedIn B2B marketing and lead generation"
    },
    {
      title: "LinkedIn Partner",
      description: "Official LinkedIn Marketing Partner with access to advanced tools and beta features"
    },
    {
      title: "Content That Converts",
      description: "Create thought leadership content that positions your brand as an industry authority"
    },
    {
      title: "Lead Quality Focus",
      description: "Optimize campaigns for high-quality B2B leads, not just vanity metrics like followers"
    }
  ];

  const faqs = [
    {
      question: "Why should B2B companies focus on LinkedIn marketing?",
      answer: "LinkedIn is the top platform for B2B marketing with 80% of B2B leads coming from LinkedIn vs other social platforms. With 930+ million professionals including decision-makers and C-suite executives, LinkedIn offers unmatched targeting capabilities by job title, company, industry, and seniority—making it essential for B2B lead generation and brand building."
    },
    {
      question: "How often should we post on LinkedIn?",
      answer: "For optimal engagement, we recommend 3-5 posts per week (12-20 per month). Consistency is more important than frequency. Our Professional plan includes 20 posts monthly with a strategic mix of company updates, thought leadership, industry insights, and promotional content to maximize reach and engagement."
    },
    {
      question: "What type of content works best on LinkedIn?",
      answer: "LinkedIn favors native content: text posts with insights/opinions, document carousels, videos, and polls. Thought leadership articles, industry trends, case studies, and employee stories perform exceptionally well. We create content that sparks professional conversations and positions your brand as an industry authority."
    },
    {
      question: "How do LinkedIn lead generation campaigns work?",
      answer: "LinkedIn Lead Gen Forms allow users to submit information without leaving LinkedIn—forms auto-populate with their profile data, dramatically increasing conversion rates. We create compelling offers (whitepapers, webinars, demos) and target precise audiences to generate high-quality B2B leads that sync directly to your CRM."
    },
    {
      question: "What's the difference between organic and paid LinkedIn marketing?",
      answer: "Organic LinkedIn (posts, articles) builds thought leadership and community but has limited reach. Paid LinkedIn (Sponsored Content, InMail, Dynamic Ads) offers precise targeting and guaranteed visibility to decision-makers. We recommend a hybrid approach: strong organic foundation amplified with targeted paid campaigns for leads."
    },
    {
      question: "Can you help with personal branding for our executives?",
      answer: "Absolutely! Executive thought leadership is crucial for B2B credibility. Our Enterprise plan includes personal branding for C-suite and key executives—optimizing profiles, ghostwriting posts, engaging with industry conversations, and building their authority. Executives with strong personal brands drive 5x more engagement for company content."
    },
    {
      question: "How much does LinkedIn advertising cost?",
      answer: "LinkedIn ad costs are higher than other platforms due to professional targeting—typically ₹400-₹800 per click or ₹600-₹1200 per lead depending on industry and targeting. However, lead quality is significantly higher. We recommend minimum ₹50,000 monthly ad spend to gather meaningful data and see results."
    },
    {
      question: "What results can we expect from LinkedIn marketing?",
      answer: "Results vary by industry and budget, but typically expect 10-20% increase in follower growth monthly, 3-5% engagement rate on posts, and ₹2000-₹5000 cost per B2B lead (much lower than traditional methods). We focus on quality metrics: leads, website traffic from LinkedIn, and engagement from target decision-makers."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A66C2] to-[#004182] text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              LinkedIn Marketing
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              LinkedIn marketing helps your business connect with professionals, decision-makers, and high-value clients through meaningful content, targeted ads, and real conversations. It's the most effective platform for B2B growth—whether you want more leads, more credibility, or more visibility in your industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                className="bg-white text-[#0A66C2] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                View Pricing
              </a>
              <a
                href="#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#0A66C2] transition-colors inline-block"
              >
                Get Free Strategy Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">
              What is LinkedIn Marketing?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                Unlike regular social media, LinkedIn is built for business. With the right strategy from a trusted linkedin marketing company, you can position your brand as an expert, attract the right audience, and open doors for partnerships, sales, and long-term relationships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our LinkedIn Marketing Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: FiBriefcase,
                title: "Content Creation",
                description: "Professional posts, carousels, and thought-leadership content tailored to your brand tone and industry audience."
              },
              {
                icon: FiTrendingUp,
                title: "LinkedIn Ads",
                description: "Smart targeting to reach CEOs, founders, HR teams, and key decision-makers with optimized ad creatives and messaging."
              },
              {
                icon: FiUsers,
                title: "Community Engagement",
                description: "Active replies, comments, and conversation management to keep your audience engaged and your brand visible."
              },
              {
                icon: FiTarget,
                title: "Lead Generation Strategy",
                description: "Personalized outreach, nurturing workflows, and connection-building designed to bring high-quality B2B leads to your inbox."
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-[#0A66C2] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 text-[#0A66C2]">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-[#0A66C2] text-white shadow-2xl scale-105 border-4 border-[#0A66C2]'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#0A66C2] px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#0A66C2]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-lg ${
                      plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#0A66C2]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-gray-100' : 'text-gray-700'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#0A66C2] hover:bg-gray-100'
                      : 'bg-[#0A66C2] text-white hover:bg-[#004182]'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Choose Us for LinkedIn Marketing?
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            As a reliable LinkedIn marketing agency with years of B2B experience, we focus on creating content and campaigns that feel human, strategic, and built for conversions—not just impressions.
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: 'Professional Content & Campaigns',
                description: 'Creating professional content your audience can relate to and reaching decision-makers through targeted LinkedIn ads.',
              },
              {
                title: 'Strong Engagement',
                description: 'Building strong engagement via comments, DMs, and conversations while positioning your brand as an industry expert.',
              },
              {
                title: 'Warm B2B Leads',
                description: 'Generating warm B2B leads that actually convert and maintaining a consistent and trustworthy brand voice.',
              },
              {
                title: 'Natural Growth',
                description: 'Our goal is simple: make your brand stand out naturally and attract serious business opportunities—without forcing anything.',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#0A66C2] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#0A66C2]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-[#0A66C2] text-2xl">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-[#0A66C2] to-[#004182] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Dominate LinkedIn Marketing?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-100">
            Get a free LinkedIn strategy call and discover how to generate high-quality B2B leads
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-[#0A66C2] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Free Strategy Call
            </a>
            <a
              href="tel:+919876543210"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#0A66C2] transition-colors inline-block"
            >
              Call: +91 98765 43210
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LinkedInMarketingPage;

